/**
 * House of Aalaya — core theme behaviour.
 *
 * Contains: scroll locking, the wishlist store, header navigation and search,
 * the variant picker, and the product media gallery.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- *
   * Utilities
   * ---------------------------------------------------------------- */

  var scrollLocks = 0;

  window.aalayaLockScroll = function () {
    scrollLocks += 1;
    document.body.classList.add('is-scroll-locked');
  };

  window.aalayaUnlockScroll = function () {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) document.body.classList.remove('is-scroll-locked');
  };

  /** Traps Tab within `container` while it is open. */
  window.aalayaTrapFocus = function (container) {
    var selector =
      'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

    function onKeydown(event) {
      if (event.key !== 'Tab') return;

      var focusable = Array.prototype.filter.call(container.querySelectorAll(selector), function (el) {
        return el.offsetParent !== null;
      });
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    return function () {
      container.removeEventListener('keydown', onKeydown);
    };
  };

  /* ---------------------------------------------------------------- *
   * Wishlist — client side only, persisted to localStorage.
   * ---------------------------------------------------------------- */

  var WISHLIST_KEY = 'aalaya:wishlist';

  var Wishlist = {
    // Stored as an array of { id, handle } entries. `handle` lets the drawer
    // fetch each product to render it; earlier builds persisted bare id
    // strings, so `read` migrates those on the way out.
    read: function () {
      try {
        var raw = window.localStorage.getItem(WISHLIST_KEY);
        var parsed = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(parsed)) return [];

        return parsed
          .map(function (entry) {
            if (typeof entry === 'string') return { id: entry, handle: '' };
            if (entry && entry.id != null) {
              return { id: String(entry.id), handle: entry.handle || '' };
            }
            return null;
          })
          .filter(Boolean);
      } catch (error) {
        return [];
      }
    },

    write: function (items) {
      try {
        window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
      } catch (error) {
        /* Storage unavailable (private mode); wishlist is best effort. */
      }
    },

    has: function (id) {
      return this.read().some(function (item) {
        return item.id === String(id);
      });
    },

    toggle: function (id, handle) {
      var items = this.read();
      var index = items.findIndex(function (item) {
        return item.id === String(id);
      });

      if (index === -1) items.push({ id: String(id), handle: handle || '' });
      else items.splice(index, 1);

      this.write(items);
      this.sync();
      return index === -1;
    },

    /** Reflects stored state onto every toggle button and the header count. */
    sync: function () {
      var items = this.read();

      document.querySelectorAll('[data-wishlist-toggle]').forEach(function (button) {
        button.setAttribute(
          'aria-pressed',
          items.some(function (item) { return item.id === String(button.dataset.productId); })
            ? 'true'
            : 'false'
        );
      });

      document.querySelectorAll('[data-wishlist-count]').forEach(function (node) {
        node.textContent = items.length;
        node.hidden = items.length === 0;
      });
    }
  };

  window.aalayaWishlist = Wishlist;

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    Wishlist.toggle(button.dataset.productId, button.dataset.productHandle);
  });

  /* ---------------------------------------------------------------- *
   * Wishlist drawer — renders saved products by fetching each one.
   * ---------------------------------------------------------------- */

  var strings = (window.aalaya && window.aalaya.strings) || {};

  /** Formats cents using the store's money format (captured from Liquid). */
  function formatMoney(cents) {
    var format = (window.aalaya && window.aalaya.moneyFormat) || '${{amount}}';
    var lang = document.documentElement.lang || 'en';
    var value = (cents / 100).toLocaleString(lang, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    var noDecimals = Math.round(cents / 100).toLocaleString(lang);

    return format
      .replace(/\{\{\s*amount\s*\}\}/g, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals);
  }

  function wishlistRow(product) {
    var li = document.createElement('li');
    li.className = 'wishlist-line';
    li.dataset.productId = product.id;

    var image = product.featured_image || (product.images && product.images[0]) || '';
    var media = image
      ? '<a class="wishlist-line__media" href="' + product.url + '">' +
          '<img src="' + image + '&width=128" alt="" loading="lazy" width="64" height="80"></a>'
      : '<span class="wishlist-line__media"></span>';

    li.innerHTML =
      media +
      '<div class="wishlist-line__info">' +
        '<p class="wishlist-line__title"><a href="' + product.url + '"></a></p>' +
        '<p class="wishlist-line__price"></p>' +
      '</div>' +
      '<button type="button" class="wishlist-line__remove" data-wishlist-remove ' +
        'data-product-id="' + product.id + '" aria-label="' + (strings.wishlistRemove || 'Remove') + '">' +
        '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" ' +
        'stroke-width="1.5" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
      '</button>';

    // Assign text via textContent so product titles can't inject markup.
    li.querySelector('.wishlist-line__title a').textContent = product.title;
    li.querySelector('.wishlist-line__price').textContent = formatMoney(product.price);
    return li;
  }

  var wishlistRenderToken = 0;

  function renderWishlistDrawer() {
    var list = document.querySelector('[data-wishlist-list]');
    if (!list) return;

    var empty = document.querySelector('[data-wishlist-empty]');
    var loading = document.querySelector('[data-wishlist-loading]');
    var items = Wishlist.read().filter(function (item) { return item.handle; });

    var token = ++wishlistRenderToken;
    list.innerHTML = '';
    if (empty) empty.hidden = items.length !== 0;
    if (loading) loading.hidden = items.length === 0;

    items.forEach(function (item) {
      fetch('/products/' + encodeURIComponent(item.handle) + '.js')
        .then(function (response) { return response.ok ? response.json() : null; })
        .catch(function () { return null; })
        .then(function (product) {
          // A newer render (or a removal) superseded this fetch — drop it.
          if (token !== wishlistRenderToken || !product) return;
          list.appendChild(wishlistRow(product));
          if (loading) loading.hidden = true;
        });
    });
  }

  // Render lazily when the drawer opens, and keep it fresh as items change.
  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-overlay-open="WishlistDrawer"]');
    if (opener) renderWishlistDrawer();

    var remove = event.target.closest('[data-wishlist-remove]');
    if (remove) {
      event.preventDefault();
      Wishlist.toggle(remove.dataset.productId);
      renderWishlistDrawer();
    }
  });

  /* ---------------------------------------------------------------- *
   * Predictive search — live product suggestions in the search drawer.
   *
   * Debounced calls to Shopify's Suggest API render matching products as
   * the shopper types; the form still submits to the full results page.
   * ---------------------------------------------------------------- */

  var PREDICTIVE_MIN = 2;
  var predictiveTimer = null;
  var predictiveController = null;

  function predictiveNodes() {
    return {
      wrap: document.querySelector('[data-predictive]'),
      list: document.querySelector('[data-predictive-list]'),
      heading: document.querySelector('[data-predictive-heading]'),
      empty: document.querySelector('[data-predictive-empty]'),
      viewAll: document.querySelector('[data-predictive-viewall]')
    };
  }

  function predictiveRow(product) {
    var li = document.createElement('li');
    li.className = 'predictive-line';

    var image = product.image || (product.featured_image && product.featured_image.url) || '';
    var media = image
      ? '<img class="predictive-line__media" src="' + image +
          (image.indexOf('?') === -1 ? '?' : '&') + 'width=104" alt="" loading="lazy" width="52" height="64">'
      : '<span class="predictive-line__media"></span>';

    // Suggest API returns price as a decimal string (e.g. "949.00"); money
    // formatting works in cents, so scale up before formatting.
    var priceCents = Math.round(parseFloat(product.price) * 100);
    var priceMarkup = isNaN(priceCents) ? '' : '<span class="predictive-line__price"></span>';

    var link = document.createElement('a');
    link.href = product.url;
    link.innerHTML = media + '<span class="predictive-line__title"></span>' + priceMarkup;

    // textContent, so product titles can't inject markup.
    link.querySelector('.predictive-line__title').textContent = product.title;
    if (!isNaN(priceCents)) {
      link.querySelector('.predictive-line__price').textContent = formatMoney(priceCents);
    }

    li.appendChild(link);
    return li;
  }

  function renderPredictive(products, query, form) {
    var nodes = predictiveNodes();
    if (!nodes.wrap) return;

    nodes.list.innerHTML = '';
    products.forEach(function (product) { nodes.list.appendChild(predictiveRow(product)); });

    var hasResults = products.length > 0;
    if (nodes.heading) nodes.heading.hidden = !hasResults;
    if (nodes.empty) nodes.empty.hidden = hasResults;

    if (nodes.viewAll) {
      var action = (form && form.getAttribute('action')) || nodes.viewAll.getAttribute('href');
      nodes.viewAll.href = action + '?q=' + encodeURIComponent(query) + '&type=product';
      nodes.viewAll.hidden = !hasResults;
    }

    nodes.wrap.hidden = false;
    nodes.wrap.removeAttribute('aria-busy');
  }

  function runPredictiveSearch(query, form, input) {
    var base = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
    var params =
      'q=' + encodeURIComponent(query) +
      '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last';

    if (predictiveController) predictiveController.abort();
    predictiveController = 'AbortController' in window ? new AbortController() : null;

    var nodes = predictiveNodes();
    if (nodes.wrap) {
      nodes.wrap.hidden = false;
      nodes.wrap.setAttribute('aria-busy', 'true');
    }

    fetch(base + '.json?' + params, {
      signal: predictiveController ? predictiveController.signal : undefined,
      headers: { Accept: 'application/json' }
    })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (data) {
        var results = data && data.resources && data.resources.results;
        renderPredictive((results && results.products) || [], query, form);
        if (input) input.setAttribute('aria-expanded', 'true');
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        // Network hiccup: hide suggestions and fall back to the normal submit.
        var wrap = predictiveNodes().wrap;
        if (wrap) { wrap.hidden = true; wrap.removeAttribute('aria-busy'); }
      });
  }

  function clearPredictive(input) {
    window.clearTimeout(predictiveTimer);
    if (predictiveController) { predictiveController.abort(); predictiveController = null; }

    var wrap = predictiveNodes().wrap;
    if (wrap) { wrap.hidden = true; wrap.removeAttribute('aria-busy'); }
    if (input) input.setAttribute('aria-expanded', 'false');
  }

  document.addEventListener('input', function (event) {
    var input = event.target.closest('[data-predictive-input]');
    if (!input) return;

    var query = input.value.trim();
    if (query.length < PREDICTIVE_MIN) {
      clearPredictive(input);
      return;
    }

    window.clearTimeout(predictiveTimer);
    predictiveTimer = window.setTimeout(function () {
      runPredictiveSearch(query, input.form, input);
    }, 220);
  });

  // Reset suggestions whenever the search drawer closes.
  document.addEventListener('aalaya:overlay:closed', function (event) {
    if (event.target.id !== 'SearchDrawer') return;
    var input = document.querySelector('[data-predictive-input]');
    if (input) clearPredictive(input);
  });

  /* ---------------------------------------------------------------- *
   * Overlays — drawers and modals.
   *
   * Listeners are delegated from `document` and resolve their target lazily,
   * so sections re-rendered by the theme editor stay wired up without
   * rebinding (which would leave handlers pointing at detached nodes).
   * ---------------------------------------------------------------- */

  var overlayState = new WeakMap();

  function openOverlay(target, focusSelector) {
    if (!target || target.dataset.open === 'true') return;

    target.dataset.open = 'true';
    window.aalayaLockScroll();

    overlayState.set(target, {
      releaseFocus: window.aalayaTrapFocus(target),
      lastFocused: document.activeElement
    });

    var field = focusSelector ? target.querySelector(focusSelector) : null;
    if (field) window.setTimeout(function () { field.focus(); }, 60);
  }

  function closeOverlay(target) {
    if (!target || target.dataset.open !== 'true') return;

    target.dataset.open = 'false';
    window.aalayaUnlockScroll();

    var state = overlayState.get(target);
    if (state) {
      if (state.releaseFocus) state.releaseFocus();
      if (state.lastFocused && state.lastFocused.focus) state.lastFocused.focus();
      overlayState.delete(target);
    }

    target.dispatchEvent(new CustomEvent('aalaya:overlay:closed', { bubbles: true }));
  }

  window.aalayaOverlay = { open: openOverlay, close: closeOverlay };

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-overlay-open]');
    if (opener) {
      event.preventDefault();
      openOverlay(document.getElementById(opener.dataset.overlayOpen), opener.dataset.overlayFocus);
      return;
    }

    var closer = event.target.closest('[data-overlay-close]');
    if (closer) {
      event.preventDefault();
      closeOverlay(closer.closest('[data-overlay]'));
      return;
    }

    if (event.target.matches('.drawer__backdrop, [data-overlay-backdrop]')) {
      closeOverlay(event.target.closest('[data-overlay]'));
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-overlay][data-open="true"]').forEach(closeOverlay);
  });

  /* ---------------------------------------------------------------- *
   * Tabs (featured products)
   * ---------------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    var tab = event.target.closest('[data-tab-target]');
    if (!tab) return;

    var group = tab.closest('[data-tabs]');
    if (!group) return;

    event.preventDefault();

    group.querySelectorAll('[data-tab-target]').forEach(function (button) {
      var selected = button === tab;
      button.setAttribute('aria-selected', selected ? 'true' : 'false');

      var panel = document.getElementById(button.dataset.tabTarget);
      if (panel) panel.hidden = !selected;
    });
  });

  /* ---------------------------------------------------------------- *
   * Reveal toggle (category grid "Show all")
   * ---------------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-reveal-toggle]');
    if (!toggle) return;

    event.preventDefault();

    var group = toggle.closest('.container').querySelector('[data-reveal-group]');
    if (!group) return;

    var expanded = toggle.getAttribute('aria-expanded') === 'true';
    var next = !expanded;

    group.querySelectorAll('[data-reveal-extra]').forEach(function (item) {
      item.hidden = !next;
    });

    toggle.setAttribute('aria-expanded', next ? 'true' : 'false');
    toggle.textContent = next ? toggle.dataset.labelLess : toggle.dataset.labelMore;
  });

  /* ---------------------------------------------------------------- *
   * Auto-submitting controls (collection sort)
   * ---------------------------------------------------------------- */

  document.addEventListener('change', function (event) {
    var control = event.target.closest('[data-auto-submit]');
    if (!control) return;

    var form = control.form || control.closest('form');
    if (form) form.submit();
  });

  /* ---------------------------------------------------------------- *
   * Variant picker
   * ---------------------------------------------------------------- */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', this.onVariantChange.bind(this));
    }

    get variants() {
      if (this._variants) return this._variants;
      var script = this.querySelector('[data-variants]');
      try {
        this._variants = script ? JSON.parse(script.textContent) : [];
      } catch (error) {
        this._variants = [];
      }
      return this._variants;
    }

    /** Selected option values, in option order. */
    get selectedOptions() {
      return Array.prototype.map.call(this.querySelectorAll('[data-option-index]'), function (fieldset) {
        var checked = fieldset.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onVariantChange() {
      var selected = this.selectedOptions;

      var variant = this.variants.find(function (candidate) {
        return candidate.options.every(function (option, index) {
          return option === selected[index];
        });
      });

      this.updateSelectedLabels();
      this.updateForm(variant);
      this.updatePrice(variant);
      this.updateMedia(variant);
      this.updateUrl(variant);
    }

    updateSelectedLabels() {
      this.querySelectorAll('[data-selected-value-for]').forEach(function (node) {
        var fieldset = this.querySelector('[data-option-name="' + node.dataset.selectedValueFor + '"]');
        var checked = fieldset && fieldset.querySelector('input:checked');
        if (checked) node.textContent = checked.value;
      }, this);
    }

    updateForm(variant) {
      var input = document.getElementById(this.dataset.variantInput);
      if (input) input.value = variant ? variant.id : '';

      var button = document.getElementById(this.dataset.submitButton);
      if (!button) return;

      var label = button.querySelector('.btn__label') || button;
      var strings = (window.aalaya && window.aalaya.strings) || {};

      if (!variant) {
        button.disabled = true;
        label.textContent = strings.unavailable || 'Unavailable';
      } else if (!variant.available) {
        button.disabled = true;
        label.textContent = strings.soldOut || 'Sold out';
      } else {
        button.disabled = false;
        label.textContent = strings.addToBag || 'Add to bag';
      }

      var paymentButton = document.getElementById(this.dataset.paymentButton);
      if (paymentButton) paymentButton.disabled = !variant || !variant.available;
    }

    updatePrice(variant) {
      var target = document.getElementById(this.dataset.priceTarget);
      if (!target || !variant) return;

      var regular = target.querySelector('[data-price-regular]');
      var compare = target.querySelector('[data-price-compare]');

      if (regular) regular.innerHTML = this.formatMoney(variant.price);

      if (compare) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compare.innerHTML = this.formatMoney(variant.compare_at_price);
          compare.hidden = false;
        } else {
          compare.hidden = true;
        }
      }
    }

    /** Uses the money format captured from Liquid on the picker element. */
    formatMoney(cents) {
      var format = this.dataset.moneyFormat || '${{amount}}';
      var value = (cents / 100).toLocaleString(document.documentElement.lang || 'en', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      var noDecimals = Math.round(cents / 100).toLocaleString(document.documentElement.lang || 'en');

      return format
        .replace(/\{\{\s*amount\s*\}\}/g, value)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals);
    }

    updateMedia(variant) {
      if (!variant || !variant.featured_media) return;

      var gallery = document.getElementById(this.dataset.gallery);
      if (!gallery) return;

      var thumb = gallery.querySelector('[data-media-id="' + variant.featured_media.id + '"]');
      if (thumb) thumb.click();
    }

    updateUrl(variant) {
      if (!variant || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', this.dataset.url + '?variant=' + variant.id);
    }
  }

  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ---------------------------------------------------------------- *
   * Boot
   * ---------------------------------------------------------------- */

  function init() {
    Wishlist.sync();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Re-sync wishlist state after the theme editor re-renders a section.
  document.addEventListener('shopify:section:load', function () {
    Wishlist.sync();
  });

  // Sections re-rendered by the cart / quick view also need wishlist state.
  document.addEventListener('aalaya:section:updated', function () {
    Wishlist.sync();
  });
})();
